import { useState, useEffect, useCallback } from 'react';
import { C, GREEN, SCHEDULER_BACKEND } from '../constants';
import { platformStyle } from '../platformStyle';
import ScheduleHorizon from './ScheduleHorizon';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const VISIBLE_PER_DAY = 3;

function startOfMonthISO(year, month) {
  return new Date(year, month, 1).toISOString();
}
function endOfMonthISO(year, month) {
  return new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString();
}

// Real, central publishing calendar — sourced live from Buffer, Postiz, and
// YouTube (plus not-yet-fired native rows), replacing the old locally-
// guessed weekday schedule that had no relationship to what actually got
// published. Self-contained: fetches its own data for the visible month.
export default function SocialCalendar({ setMainTab, channelsActiveCount = 0, approvedQueueCount = 0 }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth());
  const [posts, setPosts] = useState([]);
  const [sourceErrors, setSourceErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [summary, setSummary] = useState(null);
  const [monthSummary, setMonthSummary] = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    const start = startOfMonthISO(year, month);
    const end = endOfMonthISO(year, month);
    try {
      // force=true bypasses the backend's 2-minute cache — use this right
      // after scheduling something, when "what I see" needs to match
      // Buffer/Postiz's real state immediately rather than up to 2 minutes
      // stale.
      const url = `${SCHEDULER_BACKEND}/calendar/posts?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}${force ? '&force=true' : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load calendar');
      setPosts(data.posts || []);
      setSourceErrors(data.sourceErrors || {});
    } catch (e) {
      setError(e.message);
      setPosts([]);
    } finally {
      setLoading(false);
    }

    try {
      const [allTime, thisMonth] = await Promise.all([
        fetch(`${SCHEDULER_BACKEND}/audit/summary`).then((r) => r.json()),
        fetch(`${SCHEDULER_BACKEND}/audit/summary?since=${new Date(year, month, 1).getTime()}`).then((r) => r.json()),
      ]);
      setSummary(allTime);
      setMonthSummary(thisMonth);
    } catch {
      setSummary(null);
      setMonthSummary(null);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  function goToPrevMonth() {
    const d = new Date(year, month - 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setExpandedDays({});
  }
  function goToNextMonth() {
    const d = new Date(year, month + 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setExpandedDays({});
  }

  const postsByDay = {};
  for (const p of posts) {
    if (!p.scheduledAt) continue;
    const d = new Date(p.scheduledAt);
    if (d.getFullYear() !== year || d.getMonth() !== month) continue;
    const day = d.getDate();
    if (!postsByDay[day]) postsByDay[day] = [];
    postsByDay[day].push(p);
  }
  for (const day of Object.keys(postsByDay)) {
    postsByDay[day].sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  }

  const activeSourceErrors = Object.entries(sourceErrors).filter(([, msg]) => msg);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = new Date(year, month, 1).getDay();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  return (
    <div>
      <ScheduleHorizon />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 18 }}>
        {[
          { n: summary ? summary.confirmedLive : '—', l: 'Total published', sub: 'All time', tab: 'log' },
          { n: monthSummary ? monthSummary.confirmedLive : '—', l: 'Published this month', sub: 'Real posts only', tab: 'log' },
          { n: channelsActiveCount, l: 'Channels active', sub: 'Manage →', tab: 'connect' },
          { n: approvedQueueCount, l: 'In approved queue', sub: 'Go to queue →', tab: 'ideas' },
        ].map((item, i) => (
          <div
            key={i}
            onClick={() => setMainTab && setMainTab(item.tab)}
            style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '13px 15px', cursor: 'pointer', transition: 'border-color .15s' }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.green)}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
          >
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{item.n}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{item.l}</div>
            <div style={{ fontSize: 11, color: C.green, marginTop: 4 }}>{item.sub}</div>
          </div>
        ))}
      </div>

      {activeSourceErrors.length > 0 && (
        <div style={{ background: '#FEF9EC', border: '1px solid #f5d78e', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#854F0B' }}>
          Some sources didn't load this time — showing what's available: {activeSourceErrors.map(([src, msg]) => `${src} (${msg})`).join(', ')}
        </div>
      )}
      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#991b1b' }}>
          {error}
        </div>
      )}

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
          <button onClick={goToPrevMonth} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13, color: C.muted }}>‹</button>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
            {new Date(year, month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            {loading && <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>loading…</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={() => load(true)} disabled={loading} title="Bypass the 2-minute cache and check Buffer/Postiz right now" style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 12, color: C.muted }}>↻ Refresh</button>
            <button onClick={goToNextMonth} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13, color: C.muted }}>›</button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', borderBottom: `1px solid ${C.border}` }}>
          {DAY_NAMES.map((d) => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, color: C.muted, padding: '9px 0', fontWeight: 600 }}>{d}</div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)' }}>
          {Array(leadingBlanks).fill(0).map((_, i) => (
            <div key={`e${i}`} style={{ minHeight: 92, background: '#fafafa', borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }} />
          ))}
          {Array(daysInMonth).fill(0).map((_, i) => {
            const day = i + 1;
            const dayPosts = postsByDay[day] || [];
            const expanded = !!expandedDays[day];
            const visible = expanded ? dayPosts : dayPosts.slice(0, VISIBLE_PER_DAY);
            const isToday = isCurrentMonth && day === today.getDate();
            return (
              <div
                key={day}
                style={{ minHeight: 92, background: isToday ? '#f0fdf4' : C.card, borderRight: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, padding: '5px 4px' }}
              >
                <div style={{ fontSize: 12, color: isToday ? GREEN : C.label, marginBottom: 3, fontWeight: isToday ? 700 : 400 }}>
                  {day}
                  {isToday && <span style={{ fontSize: 9, background: GREEN, color: '#fff', padding: '0 3px', borderRadius: 3, marginLeft: 3 }}>Today</span>}
                </div>
                {visible.map((p) => {
                  const style = platformStyle(p.platform);
                  const time = new Date(p.scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                  return (
                    <div
                      key={p.id}
                      onClick={() => setSelectedPost(p)}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 4, padding: '3px 5px', fontSize: 10, marginBottom: 2, background: style.color + '18', color: style.color, cursor: 'pointer' }}
                      title={`${p.title || p.content} — click for details`}
                    >
                      <span style={{ flexShrink: 0, background: style.color, color: '#fff', borderRadius: 3, fontSize: 8, fontWeight: 700, padding: '1px 3px', lineHeight: 1.4, letterSpacing: '0.02em' }}>{style.abbr}</span>
                      <span style={{ fontWeight: 600, flexShrink: 0 }}>{time}</span>
                      {p.thumbnailUrl && (
                        <img src={p.thumbnailUrl} alt="" style={{ width: 16, height: 16, borderRadius: 2, objectFit: 'cover', marginLeft: 'auto', flexShrink: 0 }} />
                      )}
                    </div>
                  );
                })}
                {dayPosts.length > VISIBLE_PER_DAY && (
                  <div
                    onClick={() => setExpandedDays((prev) => ({ ...prev, [day]: !prev[day] }))}
                    style={{ fontSize: 9, color: C.muted, cursor: 'pointer' }}
                  >
                    {expanded ? '^ Show less' : `+${dayPosts.length - VISIBLE_PER_DAY} more`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedPost && (
        <div onClick={() => setSelectedPost(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(520px,92vw)', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            {(() => {
              const style = platformStyle(selectedPost.platform);
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, padding: '3px 9px 3px 3px', borderRadius: 6, background: style.color + '18', color: style.color }}>
                      <span style={{ background: style.color, color: '#fff', borderRadius: 4, fontSize: 10, fontWeight: 700, padding: '2px 5px', letterSpacing: '0.02em' }}>{style.abbr}</span>
                      {style.label}
                    </span>
                    <span style={{ fontSize: 12, color: selectedPost.status === 'published' ? '#16a34a' : selectedPost.status === 'failed' ? '#dc2626' : C.muted }}>
                      {selectedPost.status}
                    </span>
                    <span style={{ fontSize: 12, color: C.muted, marginLeft: 'auto' }}>
                      {new Date(selectedPost.scheduledAt).toLocaleString()}
                    </span>
                    <button onClick={() => setSelectedPost(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: C.muted }}>✕</button>
                  </div>
                  {selectedPost.thumbnailUrl && (
                    <img src={selectedPost.thumbnailUrl} alt="" style={{ width: '100%', borderRadius: 8, marginBottom: 10 }} />
                  )}
                  {selectedPost.title && <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>{selectedPost.title}</div>}
                  <div style={{ fontSize: 13, color: C.text, whiteSpace: 'pre-wrap', marginBottom: 14 }}>{selectedPost.content}</div>
                  {selectedPost.realLink ? (
                    <a href={selectedPost.realLink} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#185FA5', fontWeight: 600 }}>Go to post →</a>
                  ) : (
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>Not published yet — no link available.</div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
